import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Badge } from '@devkit/ui';
import { Key, AlertTriangle, Calendar, User, Shield } from 'lucide-react';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

interface JwtPart {
  label: string;
  value: string;
  parsed: Record<string, unknown>;
}

/** Render-gate helper: a JWT claim is "present" if it is a non-null,
 *  defined, non-empty value. Wrapping it in Boolean() satisfies TS
 *  for the `unknown` value coming from JSON.parse. */
const has = (v: unknown): boolean => Boolean(v);

export default function JwtDecoderPage() {
  const [token, setToken] = useState('');
  const [parts, setParts] = useState<{ header: JwtPart; payload: JwtPart; signature: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decode = useCallback(() => {
    setError(null);
    setParts(null);

    if (!token.trim()) {
      return;
    }

    try {
      const segments = token.trim().split('.');
      if (segments.length !== 3) {
        throw new Error('Invalid JWT format. Expected 3 segments separated by dots.');
      }

      const [headerB64, payloadB64, signature] = segments;

      // Decode header
      const headerJson = atob(headerB64.replace(/-/g, '+').replace(/_/g, '/'));
      const header = JSON.parse(headerJson);

      // Decode payload
      const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);

      setParts({
        header: { label: 'Header', value: headerB64, parsed: header },
        payload: { label: 'Payload', value: payloadB64, parsed: payload },
        signature,
      });
      trackToolExecute('jwt-decoder', 'decode');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  const isExpired = (timestamp: number) => {
    return timestamp * 1000 < Date.now();
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const clearAll = () => {
    setToken('');
    setParts(null);
    setError(null);
    trackClear('jwt-decoder');
  };

  return (
    <ToolLayout slug="jwt-decoder">
      {/* Privacy Warning — kept inline so it sits above the input. */}
      <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-300 font-medium">Decoding only - No signature verification</p>
          <p className="text-sm text-amber-400/80 mt-1">
            This tool decodes JWT tokens but does NOT verify their signatures. 
            Never trust a JWT without proper server-side verification.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              JWT Token
            </label>
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your JWT token here (e.g., eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIn0.dozjgNryP4J3jVmNHl0w5N_XgL0n3I9PlFUP0THsR8U)"
              className="w-full h-32 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={decode} className="gap-2">
              <Key className="w-4 h-4" />
              Decode
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Decoded Parts */}
          {parts && (
            <div className="space-y-4">
              {/* Header */}
              <div className="p-4 bg-surface rounded-lg border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-white">{parts.header.label}</h3>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg font-mono text-sm">
                  <pre className="text-slate-300 whitespace-pre-wrap">
                    {JSON.stringify(parts.header.parsed, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Payload */}
              <div className="p-4 bg-surface rounded-lg border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-white">{parts.payload.label}</h3>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg font-mono text-sm">
                  <pre className="text-slate-300 whitespace-pre-wrap">
                    {JSON.stringify(parts.payload.parsed, null, 2)}
                  </pre>
                </div>

                {/* Standard Claims */}
                <div className="mt-4 space-y-2">
                  {has(parts.payload.parsed.exp) && (
                    <div className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                      <span className="text-sm text-slate-400 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Expiration (exp)
                      </span>
                      <div className="text-right">
                        <span className="text-sm text-slate-300 font-mono">
                          {formatTimestamp(parts.payload.parsed.exp as number)}
                        </span>
                        {isExpired(parts.payload.parsed.exp as number) && (
                          <Badge variant="danger" className="ml-2">Expired</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  {has(parts.payload.parsed.iat) && (
                    <div className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                      <span className="text-sm text-slate-400 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Issued At (iat)
                      </span>
                      <span className="text-sm text-slate-300 font-mono">
                        {formatTimestamp(parts.payload.parsed.iat as number)}
                      </span>
                    </div>
                  )}
                  {has(parts.payload.parsed.sub) && (
                    <div className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                      <span className="text-sm text-slate-400">Subject (sub)</span>
                      <span className="text-sm text-slate-300 font-mono">
                        {parts.payload.parsed.sub as string}
                      </span>
                    </div>
                  )}
                  {has(parts.payload.parsed.iss) && (
                    <div className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                      <span className="text-sm text-slate-400">Issuer (iss)</span>
                      <span className="text-sm text-slate-300 font-mono">
                        {parts.payload.parsed.iss as string}
                      </span>
                    </div>
                  )}
                  {has(parts.payload.parsed.aud) && (
                    <div className="flex items-center justify-between p-2 bg-slate-800/30 rounded">
                      <span className="text-sm text-slate-400">Audience (aud)</span>
                      <span className="text-sm text-slate-300 font-mono">
                        {parts.payload.parsed.aud as string}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Signature */}
              <div className="p-4 bg-surface rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-primary" />
                    <h3 className="font-medium text-white">Signature</h3>
                  </div>
                  <CopyButton text={parts.signature} onCopy={() => trackCopy('jwt-decoder')} />
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg font-mono text-sm text-slate-300 break-all">
                  {parts.signature}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
